import { useCallback, useState } from 'react';

/**
 * Which optional list columns are shown.
 *
 * Persisted per browser rather than on the view: the server's
 * `display.properties` contract exists but is not wired through the client yet,
 * so this deliberately does not claim to be a shared setting.
 */
export const OPTIONAL_COLUMNS = ['state', 'priority', 'dates', 'assignee', 'labels'] as const;

export type OptionalColumn = (typeof OPTIONAL_COLUMNS)[number];
export type ColumnVisibility = Record<OptionalColumn, boolean>;

export const COLUMN_LABELS: Record<OptionalColumn, string> = {
  state: 'State',
  priority: 'Priority',
  dates: 'Dates',
  assignee: 'Assignee',
  labels: 'Labels',
};

const DEFAULT_VISIBILITY: ColumnVisibility = {
  state: true,
  priority: true,
  dates: true,
  assignee: true,
  labels: true,
};

const STORAGE_KEY = 'bb_backlog_columns';

function isColumnVisibility(value: unknown): value is ColumnVisibility {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return OPTIONAL_COLUMNS.every((column) => typeof record[column] === 'boolean');
}

export function readColumnVisibility(): ColumnVisibility {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch (err) {
    // Private browsing blocks storage access; the defaults are still correct.
    return { ...DEFAULT_VISIBILITY };
  }
  if (raw === null) return { ...DEFAULT_VISIBILITY };
  try {
    const parsed: unknown = JSON.parse(raw);
    // A stale or hand-edited value is not trustworthy, so fall back wholesale
    // rather than merging unknown keys into the visibility record.
    return isColumnVisibility(parsed) ? parsed : { ...DEFAULT_VISIBILITY };
  } catch (err) {
    return { ...DEFAULT_VISIBILITY };
  }
}

function writeColumnVisibility(value: ColumnVisibility): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (err) {
    // The choice still applies for this session; only persistence is lost.
    console.warn('Could not save the column choice.', err);
  }
}

export function useColumnVisibility(): {
  visible: ColumnVisibility;
  setColumn: (column: OptionalColumn, next: boolean) => void;
} {
  const [visible, setVisible] = useState<ColumnVisibility>(readColumnVisibility);

  const setColumn = useCallback((column: OptionalColumn, next: boolean) => {
    setVisible((curr) => {
      const updated = { ...curr, [column]: next };
      writeColumnVisibility(updated);
      return updated;
    });
  }, []);

  return { visible, setColumn };
}
